/**
 * @greenbook/client — a typed, clean-room client for the OpenMarkets Flow API.
 *
 * `OpenMarketsClient` wires the HTTP core to per-resource helpers and hands out
 * `StreamClient` instances that share the same key. Everything is exported so the
 * proxy server, smoke script, and future CLI/bot can reuse it.
 */
import { HttpClient } from "./http.js";
import { StreamClient } from "./stream.js";
import { LeaguesResource } from "./resources/leagues.js";
import { ParticipantsResource } from "./resources/participants.js";
import { ContestsResource } from "./resources/contests.js";
import { MarketsResource } from "./resources/markets.js";
import { LiquidityResource } from "./resources/liquidity.js";
import { DepthResource } from "./resources/depth.js";
import { ContestStateResource } from "./resources/contest-state.js";
import { PartnersResource } from "./resources/partners.js";
import { AuthResource } from "./resources/auth.js";
import { AccountResource } from "./resources/account.js";
import { OpportunitiesResource } from "./resources/opportunities.js";
import { ModelsResource } from "./resources/models.js";
import { ArbitragesResource } from "./resources/arbitrages.js";
import { OrdersResource } from "./resources/orders.js";
import { SignalsResource } from "./resources/signals.js";
import { McpClient } from "./mcp.js";
export class OpenMarketsClient {
    http;
    leagues;
    participants;
    contests;
    markets;
    liquidity;
    depth;
    contestState;
    partners;
    auth;
    account;
    opportunities;
    models;
    orders;
    arbitrages;
    signals;
    /** Hosted-MCP bridge — positions only (no REST equivalent). */
    mcp;
    apiKey;
    streamUrl;
    constructor(opts) {
        this.apiKey = opts.apiKey;
        this.streamUrl = opts.streamUrl;
        this.http = new HttpClient({
            apiKey: opts.apiKey,
            baseUrl: opts.baseUrl,
            fetch: opts.fetch,
            validate: opts.validate,
            onRateLimit: opts.onRateLimit,
            onSchemaMismatch: opts.onSchemaMismatch,
        });
        this.leagues = new LeaguesResource(this.http);
        this.participants = new ParticipantsResource(this.http);
        this.contests = new ContestsResource(this.http);
        this.markets = new MarketsResource(this.http);
        this.liquidity = new LiquidityResource(this.http);
        this.depth = new DepthResource(this.http);
        this.contestState = new ContestStateResource(this.http);
        this.partners = new PartnersResource(this.http);
        this.auth = new AuthResource(this.http);
        this.account = new AccountResource(this.http);
        this.opportunities = new OpportunitiesResource(this.http);
        this.models = new ModelsResource(this.http);
        this.orders = new OrdersResource(this.http);
        this.arbitrages = new ArbitragesResource(this.http);
        this.signals = new SignalsResource(this.http);
        this.mcp = new McpClient({ apiKey: opts.apiKey, url: opts.mcpUrl, fetch: opts.fetch });
    }
    /** Rate-limit snapshot from the most recent REST response. */
    get lastRateLimit() {
        return this.http.lastRateLimit;
    }
    /** Create a WebSocket stream client sharing this client's API key. */
    stream(opts = {}) {
        return new StreamClient({
            apiKey: this.apiKey,
            url: opts.url ?? this.streamUrl,
            ...opts,
        });
    }
}
/* ------------------------------------------------------------------ exports */
export { HttpClient } from "./http.js";
export { OpenMarketsError, parseErrorEnvelope } from "./errors.js";
export { paginate } from "./pagination.js";
export { schemaMismatchEntry, serializeFrictionLine, truncateSample, } from "./friction.js";
export { StreamClient } from "./stream.js";
export * from "./schemas/index.js";
export { LeaguesResource } from "./resources/leagues.js";
export { ParticipantsResource } from "./resources/participants.js";
export { ContestsResource } from "./resources/contests.js";
export { MarketsResource } from "./resources/markets.js";
export { LiquidityResource } from "./resources/liquidity.js";
export { DepthResource } from "./resources/depth.js";
export { ContestStateResource } from "./resources/contest-state.js";
export { PartnersResource } from "./resources/partners.js";
export { AuthResource } from "./resources/auth.js";
export { AccountResource } from "./resources/account.js";
export { OpportunitiesResource } from "./resources/opportunities.js";
export { ModelsResource } from "./resources/models.js";
export { ArbitragesResource } from "./resources/arbitrages.js";
export { OrdersResource, generateIdempotencyKey } from "./resources/orders.js";
export { McpClient } from "./mcp.js";
//# sourceMappingURL=index.js.map